import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import {
	C4ListResponse,
	IS_NOT_NULL_OR_UNDEFINED,
	IS_NULL_OR_UNDEFINED,
	ProjectDTO,
	RestService,
} from 'cargo-chief-commons';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { UrlService } from '@service/url/url.service';
import { UtilService } from '@service/util/util.service';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';

import { finalize } from 'rxjs/operators';
import { MtxPopoverTrigger } from '@ng-matero/extensions/popover';
import { getProjectsFilters } from '../../v2/utils/projects-list';
import { buildRequest } from '../../v2/procurement/procurement-list/build-projects-request';

@Component({
	selector: 'app-project-popover',
	templateUrl: './project-popover.component.html',
	styleUrls: ['./project-popover.component.scss'],
})
export class ProjectPopoverComponent implements OnInit {
	@Input() selectedProject: ProjectDTO = null;
	@Input() placeholder: string = 'Project';
	@Input() customFormFieldClass: string = 'custom-form-field';
	@Input() showCloseIcon: boolean = false;
	@Input() getProjectCarrier: boolean = false;
	@Input() userCarrierCount: boolean = false;
	@Input() carrierCount: number = 1;
	@Input() useWithCustomer: boolean = false;
	@Input() isMultiSelectFilter: boolean = false;
	@Input() selectedProjectIds = [];

	@Output() projectSelectedEvent: EventEmitter<ProjectDTO | string[]> = new EventEmitter();
	@Output() carriersAddedToProject: EventEmitter<any> = new EventEmitter();

	@ViewChild(MtxPopoverTrigger) trigger: MtxPopoverTrigger;

	accountId: number = null;
	searchText;
	projects: Array<ProjectDTO> = null;
	hasNextPage: boolean = false;
	isLoadingProjectsList: boolean = false;
	page = 0;
	pageSize = 20;

	constructor(
		private restService: RestService,
		private urlService: UrlService,
		private cargochief: Cargochief,
		public utilService: UtilService,
		private accountSettingsService: AccountSettingsService
	) {}

	ngOnInit(): void {
		this.accountId = this.cargochief.ACCOUNT_ID;
		this.getProjects();
		if (this.isMultiSelectFilter && this.selectedProjectIds.length) {
			this.placeholder = `Project (${this.selectedProjectIds.length})`;
		}
	}

	ngOnChanges() {
		if (IS_NULL_OR_UNDEFINED(this.selectedProject)) {
			this.placeholder = 'Project';
		} else {
			this.placeholder = this.selectedProject?.name;
		}
	}

	viewMoreProjects() {
		this.page += 1;
		this.getProjects();
	}

	getProjects() {
		this.isLoadingProjectsList = true;
		const filteredFilters = getProjectsFilters(
			[],
			this.accountSettingsService,
			this.utilService.isUserRoleAdmin(),
			this.cargochief.USER_ID
		);
		let request = buildRequest(
			this.cargochief.ACCOUNT_ID,
			this.accountSettingsService.ANNUAL_VOLUME,
			filteredFilters || [],
			this.page,
			this.pageSize,
			'created',
			'desc',
			[],
			this.cargochief.USER_ID,
			[],
			[]
		);

		if (this.userCarrierCount) {
			request.carrierCount = this.carrierCount;
		}
		if (this.useWithCustomer) {
			request.havingCustomer = true;
		}
		const url = this.urlService.getProjectsList();
		this.restService
			.POST(url, request)
			.pipe(
				finalize(() => {
					this.isLoadingProjectsList = false;
				})
			)
			.subscribe(
				(response: C4ListResponse<ProjectDTO>) => {
					if (this.projects) {
						this.projects = this.projects.concat(response.data);
					} else {
						this.projects = response.data;
					}
					this.hasNextPage = response.nextPage;
				},
				(error) => {
					this.projects = [];
				}
			);
	}

	projectSelected(project: ProjectDTO) {
		this.selectedProject = project;
		this.placeholder = this.selectedProject?.name;
		this.projectSelectedEvent.emit(this.selectedProject);
		if (this.getProjectCarrier) {
			this.getCarrierIdsOfFilteredProjects();
		}
		if (!this.utilService.isNullOrUndefined(this.trigger)) {
			this.trigger.closePopover();
		}
	}

	onProjectToggleSelection(project: ProjectDTO, isChecked: boolean) {
		if (isChecked) {
			this.selectedProjectIds.push(project.id);
		} else {
			this.selectedProjectIds = this.selectedProjectIds.filter((id) => id !== project.id);
		}
		this.placeholder = `Project (${this.selectedProjectIds.length})`;
	}

	removeSelectedProject() {
		this.selectedProject = null;
		this.projectSelectedEvent.emit(null);
		this.carriersAddedToProject.emit(null);
		this.placeholder = 'Project';
	}

	renderDownArrowIcon(): boolean {
		if (!this.showCloseIcon || (this.showCloseIcon && IS_NULL_OR_UNDEFINED(this.selectedProject))) {
			return true;
		}
		return false;
	}

	renderCloseArrowIcon(): boolean {
		if (this.showCloseIcon && IS_NOT_NULL_OR_UNDEFINED(this.selectedProject)) {
			return true;
		}
		return false;
	}

	getCarrierIdsOfFilteredProjects() {
		const url = this.urlService.getCarrierIdsByProjectIds();
		this.restService.POST(url, { projectIds: [this.selectedProject.id] }).subscribe(
			(result) => {
				this.carriersAddedToProject.emit(result.data.carrierIDs);
			},
			(error) => {
				this.carriersAddedToProject.emit(null);
			}
		);
	}

	isProjectSelected(project): boolean {
		return this.selectedProjectIds?.includes(project.id);
	}
}
